/**
 * @prettier
 */

import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

// Components
import CustomButton from "../../../components/CustomButton";
import DocumentFileIcon from "../../../components/ProofOfAddressColoredIcons/DocumentFileIcon";

// Images
import backArrowIcon from "../../../assets/img/back_arrow_icon.png";
import RightArrowIcon from "../../../components/ProofOfAddressColoredIcons/RightArrowIcon";
import checkMarkIcon from "../../../assets/img/check_mark.png";

// Renders document selection UI with parent and child documents
const DocumentSelectionState = ({
    primaryFocusRef,
    documents,
    additionalDocuments,
    selectedDocument,
    handleDocumentSelect,
    handleGoBack,
    brandingColor,
    poaStatusMap,
    additionalStatusMap,
}) => {
    const { t } = useTranslation();

    // Removes backslash from string
    const sanitiseString = string => string?.replace(/\\/g, " ") || "";

    // Determine if we're showing the root documents list or children of a parent
    const isShowingChildren =
        selectedDocument &&
        selectedDocument.child &&
        selectedDocument.child.length > 0;
    const documentsToShow = isShowingChildren
        ? selectedDocument.child
        : documents;

    // Check if all required documents in current child section are completed
    const isCurrentSectionCompleted = () => {
        if (!selectedDocument || !selectedDocument.child) return false;

        // Get required children
        const requiredChildren = selectedDocument.child.filter(
            doc => doc.is_required
        );

        // Get minimum required count (either from min_required_child or count of required children)
        const minRequiredCount =
            selectedDocument.min_required_child || requiredChildren.length;

        // Count completed children
        const completedCount = selectedDocument.child.filter(doc =>
            isDocumentCompleted(doc)
        ).length;

        // Check if enough documents are completed
        return (
            completedCount >= minRequiredCount &&
            // Make sure all required documents are completed
            requiredChildren.every(doc => isDocumentCompleted(doc))
        );
    };

    // Handle continue button click for child section
    const handleChildSectionComplete = () => {
        handleGoBack(); // Navigate back to parent
    };

    // Check if document is completed
    const isDocumentCompleted = doc => {
        // If the document has children, check min_required_child and individual required children
        if (doc.child && doc.child.length > 0) {
            // Count uploaded children
            const uploadedChildCount = doc.child.filter(childDoc =>
                isDocumentCompleted(childDoc)
            ).length;

            // 1. Check if min_required_child is set and satisfied
            if (doc.min_required_child && doc.min_required_child > 0) {
                if (uploadedChildCount < doc.min_required_child) {
                    return false; // Not enough children completed
                }
            } else if (doc.child.length > 0) {
                const hasRequiredChildren = doc.child.some(
                    childDoc => childDoc.is_required
                );
                if (!hasRequiredChildren && uploadedChildCount === 0) {
                    return false;
                }
            }

            // 3. Check all required children are completed (if any)
            const requiredChildren = doc.child.filter(
                childDoc => childDoc.is_required
            );
            if (requiredChildren.length > 0) {
                const allRequiredChildrenCompleted = requiredChildren.every(
                    childDoc => isDocumentCompleted(childDoc)
                );
                if (!allRequiredChildrenCompleted) {
                    return false;
                }
            }
            // If both conditions are satisfied, parent is completed
            return true;
        }

        // If no children, check status map as before
        if (
            additionalDocuments &&
            additionalDocuments.some(
                addDoc => addDoc.poa_upload_key === doc.poa_upload_key
            )
        ) {
            return (
                additionalStatusMap &&
                additionalStatusMap[doc.poa_upload_key] === true
            );
        } else {
            return poaStatusMap && poaStatusMap[doc.poa_upload_key] === true;
        }
    };

    // Count total required documents recursively, including min_required_child logic
    const countTotalRequiredDocuments = docs => {
        if (!docs || !docs.length) return 0;

        let count = 0;

        for (const doc of docs) {
            // If the document has children and a min_required_child constraint
            if (
                doc.child &&
                doc.child.length > 0 &&
                doc.min_required_child &&
                doc.min_required_child > 0
            ) {
                // Count is the max of min_required_child or the number of required children (if greater)
                const requiredChildrenCount = doc.child.filter(
                    childDoc => childDoc.is_required
                ).length;
                count += Math.max(
                    doc.min_required_child,
                    requiredChildrenCount
                );
            } else if (doc.is_required) {
                // If the document itself is required and has no min_required_child, count it
                count++;
            } else {
                // Not required, do not count
            }

            // Recursively count required documents in children
            if (doc.child && doc.child.length > 0) {
                count += countTotalRequiredDocuments(doc.child);
            }
        }

        return count;
    };

    // Render document button content - similar to the passport button approach
    const renderButtonContent = doc => {
        return (
            <>
                <div className="button-icon-wrapper">
                    <DocumentFileIcon
                        iconColor={brandingColor}
                        width={32}
                        height={38}
                    />
                </div>
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "left",
                    }}
                >
                    <span>{sanitiseString(doc.title)}</span>
                    {doc.is_required && (
                        <span className="required-tag">
                            {t("idpal_required")}
                        </span>
                    )}
                </div>
                <div
                    style={{
                        display: "flex",
                        marginLeft: "auto",
                        marginRight: "4px",
                    }}
                >
                    {isDocumentCompleted(doc) ? (
                        <img
                            src={checkMarkIcon}
                            alt="Completed"
                            width="24"
                            height="24"
                        />
                    ) : (
                        <RightArrowIcon
                            iconColor={brandingColor}
                            width={24}
                            height={24}
                        />
                    )}
                </div>
            </>
        );
    };

    // Only show additional documents on the main selection screen, not when in child view
    const showAdditionalDocuments =
        !isShowingChildren &&
        additionalDocuments &&
        additionalDocuments.length > 0;

    // Only show POA documents when we have documents to show
    const showPoaDocuments = documentsToShow && documentsToShow.length > 0;

    return (
        <>
            {selectedDocument && (
                <>
                    <h1
                        className="page-title"
                        ref={primaryFocusRef}
                        tabIndex={0}
                    >
                        {selectedDocument.title}
                    </h1>
                    <h2 className="page-subtitle">
                        {selectedDocument.sub_title}
                    </h2>
                </>
            )}

            {/* Proof of Address Documents */}
            {showPoaDocuments && (
                <>
                    {!selectedDocument && (
                        <h1
                            className="page-title"
                            ref={primaryFocusRef}
                            tabIndex={0}
                        >
                            {t("idpal_select_proof_of_address")}
                        </h1>
                    )}
                    <div className="select-document-button-container">
                        {documentsToShow.map(doc => (
                            <button
                                key={doc.poa_upload_key}
                                aria-label={sanitiseString(doc.title)}
                                onClick={() => handleDocumentSelect(doc)}
                                className={`outlined-button ${isDocumentCompleted(doc) ? "completed-document" : ""}`}
                            >
                                {renderButtonContent(doc)}
                            </button>
                        ))}
                    </div>

                    {/* Document Count Info for POA Documents */}
                    {((isShowingChildren &&
                        countTotalRequiredDocuments(documentsToShow) > 0) ||
                        (!isShowingChildren &&
                            countTotalRequiredDocuments(documents) > 0)) && (
                        <div className="document-count-info">
                            <p>
                                {isShowingChildren
                                    ? `${countTotalRequiredDocuments(documentsToShow)} ${
                                          countTotalRequiredDocuments(
                                              documentsToShow
                                          ) === 1
                                              ? t("idpal_document_is_required")
                                              : t("idpal_documents_required")
                                      }`
                                    : `${countTotalRequiredDocuments(documents)} ${
                                          countTotalRequiredDocuments(
                                              documents
                                          ) === 1
                                              ? t("idpal_document_is_required")
                                              : t("idpal_documents_required")
                                      }`}
                            </p>
                        </div>
                    )}
                </>
            )}

            {/* Additional Documents Section */}
            {showAdditionalDocuments && (
                <>
                    <h2 className="section-title">
                        {t("idpal_additional_documents")}
                    </h2>

                    <div className="select-document-button-container">
                        {additionalDocuments.map(doc => (
                            <button
                                key={doc.poa_upload_key}
                                aria-label={sanitiseString(doc.title)}
                                onClick={() => handleDocumentSelect(doc)}
                                className={`outlined-button ${isDocumentCompleted(doc) ? "completed-document" : ""}`}
                            >
                                {renderButtonContent(doc)}
                            </button>
                        ))}
                    </div>

                    {/* Document Count Info for Additional Documents */}
                    {countTotalRequiredDocuments(additionalDocuments) > 0 && (
                        <div className="document-count-info">
                            <p>
                                {`${countTotalRequiredDocuments(additionalDocuments)} ${
                                    countTotalRequiredDocuments(
                                        additionalDocuments
                                    ) === 1
                                        ? t("idpal_document_is_required")
                                        : t("idpal_documents_required")
                                }`}
                            </p>
                        </div>
                    )}
                </>
            )}

            {isShowingChildren && (
                <>
                    <div className="back-button-container-section">
                        <button
                            className="select-different-doc-btn"
                            onClick={() => {
                                handleGoBack();
                            }}
                        >
                            <img
                                src={backArrowIcon}
                                alt="Back arrow"
                                className="back-arrow-icon"
                            />
                            <span>{t("idpal_select_different_document")}</span>
                        </button>
                    </div>
                    <CustomButton
                        id="proof-of-address-navigate-continue"
                        className="btn btn__arrow"
                        label={t("idpal_continue")}
                        type="submit"
                        isDisabled={!isCurrentSectionCompleted()}
                        handleClick={handleChildSectionComplete}
                    />
                </>
            )}
        </>
    );
};

// Map Redux state to component props
function mapStateToProps(state) {
    return {
        poaStatusMap: state.poaDocuments.poaStatusMap,
        additionalStatusMap: state.poaDocuments.additionalStatusMap,
    };
}

// Map Redux actions to component props
function mapDispatchToProps(dispatch) {
    return bindActionCreators({}, dispatch);
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(DocumentSelectionState);
